import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbDateParserFormatter, NgbDateStruct, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CampaignMilestone, DriveCampaign, FeatureTypes, ProjectStatusList } from '../../../shared/interfaces';
import { FormBuilder, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ProjectV2ServiceService } from '../../../shared/services/project-v2-service.service';
import { DateHelperService } from '../../../shared/services/date-helper.service';
import { OverlayService } from '../../../shared/services/overlayService';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../shared/services/auth.service';
import { CustomDateParserFormatter } from '../../../shared/services/datepicker-adapter';
import { dateRangeValidator } from '../../../shared/validators/date-validators';
import { CampaignName, HelperService } from '../../../shared/services/helper.service';
import { FeatureConfigService } from '../../../shared/services/feature-config.service';
import { CommonModule } from '@angular/common';
import { UiSwitchModule } from 'ngx-ui-switch';
import { SharedModule } from '../../../sub-modules/SharedModule';
import { ProjectSharedModule } from '../../../sub-modules/project-modules/project-SharedModule';
import { MilestoneCommentComponent } from '../milestone-comment/milestone-comment.component';
import { NEGOTIATION_PERIODE_DAYS } from '../timeline.constants';

@Component({
  selector: 'mtm-milestone-status-editor',
  templateUrl: './milestone-status-editor.component.html',
  styleUrl: './milestone-status-editor.component.scss',
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgbModule,
    UiSwitchModule,
    SharedModule,
    ProjectSharedModule,
    MilestoneCommentComponent,
  ],
})
export class MilestoneStatusEditorComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly campaignService: ProjectV2ServiceService = inject(ProjectV2ServiceService);
  private readonly overlayService: OverlayService = inject(OverlayService);
  private readonly translatePipe: TranslatePipe = inject(TranslatePipe);
  private readonly authService: AuthService = inject(AuthService);
  private readonly featureConfigService: FeatureConfigService = inject(FeatureConfigService);
  private readonly dateHelperService: DateHelperService = inject(DateHelperService);

  private _milestone: CampaignMilestone;
  isLoading: boolean;
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  activeMilestone: boolean;
  negotiatedMaxDate: any = { year: 2050, month: 12, day: 31 };
  minNegotiatedDate: any = { year: 2010, month: 1, day: 1 };
  ngUnsubscribe = new Subject();
  authUser: any;
  minActualDate: any = { year: 2010, month: 1, day: 1 };
  actualMaxDate: any = { year: 2050, month: 12, day: 31 };
  algorithm2Enabled: boolean;
  hasNegotiatedDate: boolean = true;

  get milestone(): CampaignMilestone {
    return this._milestone;
  }

  get milestoneLabel(): string {
    return !this.milestone.activeMilestone ? 'Disabled Milestone' : 'Active Milestone';
  }

  @Input()
  set milestone(value: CampaignMilestone) {
    this._milestone = value;
    if (value.activeMilestone == null) {
      this._milestone.activeMilestone = true;
    }
    this.authUser = this.authService.getAuthUser();

    this.activeMilestone = this._milestone.activeMilestone;
    this.setNegotiatedDateRange();
    this.updateFormValues();
  }

  @Input() campaign: DriveCampaign;

  milestoneForm = this.fb.group({
    id: [
      '',
      {
        validators: [Validators.required],
      },
    ],
    driveDate: [
      null,
      {
        validators: [Validators.required],
      },
    ],
    negotiatedDate: [null, dateRangeValidator(2010, 2050)],
    actualDate: [null, dateRangeValidator(2010, 2050)],
    status: [
      ProjectStatusList.IN_PROGRESS,
      {
        validators: [Validators.required],
      },
    ],
  });

  ProjectStatus = ProjectStatusList;

  descriptionKey: string;

  get statusValue(): string {
    return this.milestoneForm.get('status').value;
  }

  ngOnInit() {
    this.descriptionKey = `milestone_explanation_${this.milestone.name}`;
    if (this.milestone.blockName) {
      this.descriptionKey = `${this.descriptionKey}_${this.milestone.blockName}`;
    }

    this.featureConfigService
      .getFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_MAIN, FeatureTypes.FEA_CAMPAIGN_TIMELINE])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.algorithm2Enabled =
            response[FeatureTypes.FEA_CAMPAIGN_TIMELINE]?.autoCalculateMilestoneStatus === 'ALGORITHM_2';
          this.hasNegotiatedDate = response[FeatureTypes.FEA_CAMPAIGN_TIMELINE]?.hasNegotiatedDate;
          this.setNegotiatedDateRange();
        },
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  private setNegotiatedDateRange(): void {
    if (!this.algorithm2Enabled || !this.milestone.driveDate) {
      return;
    }

    this.negotiatedMaxDate = this.dateHelperService.fromDateToDatepicker(
      moment(this.milestone.driveDate).add(NEGOTIATION_PERIODE_DAYS, 'days').toDate()
    );
    this.minNegotiatedDate = this.dateHelperService.fromDateToDatepicker(moment(this.milestone.driveDate).toDate());
  }

  private updateFormValues() {
    if (!this.milestone) {
      return;
    }

    const value: Partial<typeof this.milestoneForm.value> = {
      id: this.milestone.id,
      status: this.milestone.status || ProjectStatusList.IN_PROGRESS,
    };

    if (this.milestone.driveDate) {
      value.driveDate = this.dateHelperService.fromDateToDatepicker(new Date(this.milestone.driveDate));
    }
    if (this.milestone.negotiatedDate) {
      value.negotiatedDate = this.dateHelperService.fromDateToDatepicker(new Date(this.milestone.negotiatedDate));
    }
    if (this.milestone.actualDate) {
      value.actualDate = this.dateHelperService.fromDateToDatepicker(new Date(this.milestone.actualDate));
    }

    this.milestoneForm.patchValue(value);
  }

  cancel() {
    this.activeModal.close(this.milestoneForm.value);
  }

  save() {
    if (!this.milestoneForm.valid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const newMilestone = this.milestoneForm.value;
    const actualDate = newMilestone.actualDate
      ? this.dateHelperService.fromDateToUTCString(this.dateHelperService.fromDatePickerToDate(newMilestone.actualDate))
      : null;
    const negotiatedDate = newMilestone.negotiatedDate
      ? this.dateHelperService.fromDateToUTCString(
          this.dateHelperService.fromDatePickerToDate(newMilestone.negotiatedDate)
        )
      : null;
    this.campaignService
      .updateDateStatusMilestone(this.campaign.id, {
        id: newMilestone.id,
        actualDate,
        negotiatedDate,
        activeMilestone: this.activeMilestone,
      })
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          const timelines = JSON.parse(res.timelines) as any[];
          this.campaign.timelines = JSON.stringify(timelines);
          this.activeModal.close(newMilestone);
        },
        error: (error) => {
          this.isLoading = false;
          this.overlayService.showError(this.translatePipe.transform('overlaySaveError'));
        },
      });
  }
}
